import { API } from "aws-amplify";
import Auth from "@aws-amplify/auth";
import { store } from "../../store";

const getHeaders = async () => {
  const user = await Auth.currentAuthenticatedUser();
  const jwtToken = user.signInUserSession?.accessToken?.jwtToken;
  const headers = {};
  if (jwtToken) headers['Authorization'] = 'Bearer ' + jwtToken;
  const { loginReducer } = store.getState();
  const clientId = loginReducer?.groups?.[0];
  if (clientId) headers['BluStream-Client-ID'] = clientId;
  return headers;
};

const customerUuid = () => {
  const { loginReducer } = store.getState();
  return loginReducer?.groups?.[0];
};

// Recursively remap TopicHierarchyNode (backend) fields to what components expect.
// Backend: { displayName, conversationCount, children, systemTopic, ... }
// Components expect: { name, conversationCount, disabled, children, ... }
function normalizeTopicNode(node) {
  return {
    ...node,
    name: node.displayName,
    ownConversationCount: node.ownConversationCount || 0,
    disabled: node.status === 'disabled',
    children: (node.children || []).map(normalizeTopicNode),
  };
}

// GET /api/topics/customers/{customerUuid}/hierarchy?period=this_month
// Returns List<TopicHierarchyNode> — normalized for component consumption
export const getTopics = async (period = 'this_month') => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  const data = await API.get("blustreamREST", `/gandalfproxy/api/topics/customers/${uuid}/hierarchy?period=${period}`, { headers });
  return (Array.isArray(data) ? data : []).map(normalizeTopicNode);
};

// GET /api/topics/{topicId}/detail?customerUuid=...&period=this_month
// Backend returns: { topic: TopicSummary, conversationCount, conversations: ConversationListItem[] }
// Adapted to: { conversationCount, highConfidencePct, medConfidencePct, lowConfidencePct, avgConfidence }
export const getTopicStats = async (topicId, period = 'this_month') => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  const res = await API.get("blustreamREST", `/gandalfproxy/api/topics/${topicId}/detail?customerUuid=${uuid}&period=${period}`, { headers });

  const conversationCount = res.conversationCount || 0;
  const conversations = res.conversations || [];

  // Each ConversationListItem has topics[].{ topicId, confidence (0–1 BigDecimal) }
  // Find the confidence for this specific topic assignment in each conversation
  const confidences = conversations
    .map((c) => {
      const assignment = (c.topics || []).find((t) => t.topicId === topicId);
      return assignment ? parseFloat(assignment.confidence) * 100 : null;
    })
    .filter((c) => c !== null);

  let highConfidencePct = 0, medConfidencePct = 0, lowConfidencePct = 0, avgConfidence = 0;
  if (confidences.length > 0) {
    const high = confidences.filter((c) => c >= 75).length;
    const med = confidences.filter((c) => c >= 50 && c < 75).length;
    const low = confidences.filter((c) => c < 50).length;
    highConfidencePct = Math.round((high / confidences.length) * 100);
    medConfidencePct = Math.round((med / confidences.length) * 100);
    lowConfidencePct = Math.round((low / confidences.length) * 100);
    avgConfidence = Math.round(confidences.reduce((a, b) => a + b, 0) / confidences.length);
  }

  return { conversationCount, highConfidencePct, medConfidencePct, lowConfidencePct, avgConfidence };
};

// GET /api/conversations/customers/{customerUuid}/trend?period=...&topN=5
export const getConversationTrend = async (period = 'this_quarter', topN = 5) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.get("blustreamREST", `/gandalfproxy/api/conversations/customers/${uuid}/trend?period=${period}&topN=${topN}`, { headers });
};

// PUT /api/topics/{topicId}/display-name?customerUuid=...
// Body: { "displayName": "new name" }
export const renameTopic = async (topicId, displayName) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.put("blustreamREST", `/gandalfproxy/api/topics/${topicId}/display-name?customerUuid=${uuid}`, { headers, body: { displayName } });
};

// POST /api/topics/customers/{customerUuid}/topics
// Body: { "displayName": "...", "parentTopicId": 123 }
export const addSubtopic = async (displayName, parentTopicId = null) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  const body = { displayName };
  if (parentTopicId !== null) body.parentTopicId = parentTopicId;
  return API.post("blustreamREST", `/gandalfproxy/api/topics/customers/${uuid}/topics`, { headers, body });
};

// DELETE /api/topics/{topicId}?customerUuid=...
// 204 success | 409 { error: "..." } if blocked | 400 if not found
export const disableTopic = async (topicId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.del("blustreamREST", `/gandalfproxy/api/topics/${topicId}?customerUuid=${uuid}`, { headers });
};

// GET /api/topics/{topicId}/detail?customerUuid=...&period=...
// Extracts and adapts the conversation list from TopicDetailResponse.
// Backend ConversationListItem: { id, startedAt, messageCount, topics: [{ topicId, confidence }] }
// Components expect: { conversations: [{ id, preview, classifiedAt, confidence, contactId }], total }
export const getConversations = async (topicId, period = 'this_month') => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  const res = await API.get("blustreamREST", `/gandalfproxy/api/topics/${topicId}/detail?customerUuid=${uuid}&period=${period}`, { headers });

  const raw = res.conversations || [];
  const conversations = raw.map((c) => {
    const assignment = (c.topics || []).find((t) => t.topicId === topicId);
    const confidence = assignment ? Math.round(parseFloat(assignment.confidence) * 100) : 0;
    return {
      id: c.id,
      preview: c.firstMessagePreview || `${c.messageCount || 0} message${c.messageCount !== 1 ? 's' : ''}`,
      classifiedAt: c.startedAt,
      confidence,
      contactName: c.contactName || null,
      messageCount: c.messageCount || 0,
    };
  });

  return { conversations, total: conversations.length };
};

// GET /api/conversations/{conversationId}?customerUuid=...
// Returns full conversation detail: messages, classifications, timestamps
export const getConversationDetail = async (conversationId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  const res = await API.get("blustreamREST", `/gandalfproxy/api/conversations/${conversationId}?customerUuid=${uuid}`, { headers });

  const messages = (res.messages || []).map((m) => ({
    id: m.id || String(Math.random()),
    content: m.messageBody || '',
    direction: m.role === 'user' ? 'outbound' : 'inbound',
    sentAt: m.timestamp,
  }));

  const primary = (res.classifications || [])[0];
  return {
    messages,
    topicName: primary ? primary.displayName : 'Unknown',
    confidence: primary ? Math.round(parseFloat(primary.confidence) * 100) : 0,
    classifiedAt: res.startedAt || null,
    classifications: res.classifications || [],
  };
};

// GET /api/topics/{topicId}/journeys?customerUuid=...
export const getTopicJourneys = async (topicId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.get("blustreamREST", `/gandalfproxy/api/topics/${topicId}/journeys?customerUuid=${uuid}`, { headers });
};

// POST /api/topics/{topicId}/journeys?customerUuid=...
export const addJourneyMapping = async (topicId, journeyId, coverage = 'light') => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.post("blustreamREST", `/gandalfproxy/api/topics/${topicId}/journeys?customerUuid=${uuid}`, { headers, body: { journeyId, coverage } });
};

// DELETE /api/topics/{topicId}/journeys/{mappingId}?customerUuid=...
export const removeJourneyMapping = async (topicId, mappingId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.del("blustreamREST", `/gandalfproxy/api/topics/${topicId}/journeys/${mappingId}?customerUuid=${uuid}`, { headers });
};

// PUT /api/topics/{topicId}/parent?customerUuid=...
// Body: { "parentTopicId": 123 } or { "parentTopicId": null } for root
export const moveTopic = async (topicId, newParentTopicId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.put("blustreamREST", `/gandalfproxy/api/topics/${topicId}/parent?customerUuid=${uuid}`, {
    headers,
    body: { parentTopicId: newParentTopicId },
  });
};

// PUT /api/topics/{newTopicId}/conversations/{conversationId}/assign?customerUuid=...
export const reassignConversation = async (conversationId, newTopicId) => {
  const uuid = customerUuid();
  const headers = await getHeaders();
  return API.put("blustreamREST", `/gandalfproxy/api/topics/${newTopicId}/conversations/${conversationId}/assign?customerUuid=${uuid}`, { headers });
};
